import { useQuery } from '@tanstack/react-query';
import {
  sanityClient,
  allPostsQuery,
  postBySlugQuery,
  postsByCategoryQuery,
  allCategoriesQuery,
  allTagsQuery,
  relatedPostsQuery,
  type SanityPost,
  type SanityCategory,
  type SanityTag,
} from '@/lib/sanity';

export function useInsightPosts(categorySlug?: string) {
  return useQuery({
    queryKey: ['insights', 'posts', categorySlug],
    queryFn: async (): Promise<SanityPost[]> => {
      if (categorySlug) {
        return sanityClient.fetch(postsByCategoryQuery, { categorySlug });
      }
      return sanityClient.fetch(allPostsQuery);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useInsightPost(slug: string) {
  return useQuery({
    queryKey: ['insights', 'post', slug],
    queryFn: async (): Promise<SanityPost | null> => {
      return sanityClient.fetch(postBySlugQuery, { slug });
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
}

export function useInsightCategories() {
  return useQuery({
    queryKey: ['insights', 'categories'],
    queryFn: async (): Promise<SanityCategory[]> => {
      return sanityClient.fetch(allCategoriesQuery);
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useInsightTags() {
  return useQuery({
    queryKey: ['insights', 'tags'],
    queryFn: async (): Promise<SanityTag[]> => {
      return sanityClient.fetch(allTagsQuery);
    },
    staleTime: 1000 * 60 * 30,
  });
}

export function useRelatedPosts(currentSlug: string, categoryIds: string[]) {
  return useQuery({
    queryKey: ['insights', 'related', currentSlug],
    queryFn: async (): Promise<SanityPost[]> => {
      return sanityClient.fetch(relatedPostsQuery, { currentSlug, categoryIds });
    },
    enabled: !!currentSlug && categoryIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}
